import { SaveLeadFormDraftParams } from '@plura/shared/types';
import { v4 as uuidv4 } from 'uuid';
import themes from './_themes';
import { generateCustomID } from '@plura/shared/utils';

// Export a function that generates fresh UUIDs each time it's called
const generateSoftwareDevelopmentQuizTemplate = (): SaveLeadFormDraftParams => {
  const formId = uuidv4();
  const collectionId = generateCustomID(18)
  const workspaceId = uuidv4();

  // Generate new UUIDs for all blocks to ensure uniqueness
  const welcomeScreenId = uuidv4();
  const welcomeScreenBlockId = uuidv4();
  const emailId = uuidv4();
  const emailBlockId = uuidv4();
  const contactInfoId = uuidv4();
  const contactInfoBlockId = uuidv4();
  const multipleChoice1Id = uuidv4();
  const multipleChoice1BlockId = uuidv4();
  const statement1Id = uuidv4();
  const statement1BlockId = uuidv4();
  const statement2Id = uuidv4();
  const statement2BlockId = uuidv4();
  const multipleChoice2Id = uuidv4();
  const multipleChoice2BlockId = uuidv4();
  const statement3Id = uuidv4();
  const statement3BlockId = uuidv4();
  const statement4Id = uuidv4();
  const statement4BlockId = uuidv4();
  const multipleChoice3Id = uuidv4();
  const multipleChoice3BlockId = uuidv4();
  const statement5Id = uuidv4();
  const statement5BlockId = uuidv4();
  const statement6Id = uuidv4();
  const statement6BlockId = uuidv4();
  const multipleChoice4Id = uuidv4();
  const multipleChoice4BlockId = uuidv4();
  const statement7Id = uuidv4();
  const statement7BlockId = uuidv4();
  const statement8Id = uuidv4();
  const statement8BlockId = uuidv4();
  const endScreenId = uuidv4();
  const endScreenBlockId = uuidv4();

  // Generate new UUIDs for select options - MC1
  const mc1Option1Id = uuidv4(); // Functional
  const mc1Option2Id = uuidv4(); // Declarative
  const mc1Option3Id = uuidv4(); // Circular (correct answer)
  const mc1Option4Id = uuidv4(); // Object-oriented
  const mc1Option5Id = uuidv4(); // Procedural

  // Generate new UUIDs for select options - MC2
  const mc2Option1Id = uuidv4(); // Creates a new commit (correct answer)
  const mc2Option2Id = uuidv4(); // Deletes a commit
  const mc2Option3Id = uuidv4(); // Resets your branch
  const mc2Option4Id = uuidv4(); // Stashes your changes

  // Generate new UUIDs for select options - MC3
  const mc3Option1Id = uuidv4(); // Server-Side Rendering (correct answer)
  const mc3Option2Id = uuidv4(); // Secure System Routing
  const mc3Option3Id = uuidv4(); // Scripted Static Resources

  // Generate new UUIDs for select options - MC4
  const mc4Option1Id = uuidv4(); // A function that can only return numbers
  const mc4Option2Id = uuidv4(); // A function that mutates global variables
  const mc4Option3Id = uuidv4(); // A function with no side effects (correct answer)
  const mc4Option4Id = uuidv4(); // A function that runs only once

  // Generate new UUIDs for media
  const welcomeMediaId = uuidv4();
  const statement1MediaId = uuidv4();
  const statement2MediaId = uuidv4();
  const statement3MediaId = uuidv4();
  const statement4MediaId = uuidv4();
  const statement5MediaId = uuidv4();
  const statement6MediaId = uuidv4();
  const statement7MediaId = uuidv4();
  const statement8MediaId = uuidv4();
  const endScreenMediaId = uuidv4();

  // Generate new UUIDs for logic items and conditions
  const mc1Logic1Id = uuidv4();
  const mc1Condition1Id = uuidv4();
  const mc2Logic1Id = uuidv4();
  const mc2Condition1Id = uuidv4();
  const mc3Logic1Id = uuidv4();
  const mc3Condition1Id = uuidv4();
  const mc4Logic1Id = uuidv4();
  const mc4Condition1Id = uuidv4();

  return {
    ...themes.darkMidnight,
    "collectionId": collectionId,
    "name": "Digital Marketing Quiz",
    "overview": "This is a form to collect leads",
    "about": "We're looking to connect with new leads",
    "tone": "Casual + Professional",
    "hideBuiltWithBadge": false,
    "closed": false,
    "closedMessage": "",
    "legalDisclaimer": false,
    "legalMessage": "",
    "notifications": false,
    "notificationEmail": "",
    "logoUrl": "https://s3.amazonaws.com/leadrr-prod-uploads/uploads/0d5b697e-e386-46ef-a2c7-34044737a773/GOLDLEAF-CO.png",
    "transitionType": "SLIDE_VERTICAL",
    "transitionSpeed": 500,
    "skipWelcomeScreen": false,
    "googleTagManager": "",
    "metaPixel": "",
    "assistantId": `asst_${collectionId}`,
    "fontSizeSetting": "MEDIUM",
    "customEndScreen": false,
    "hiddenFields": [],
    "journeyItems": [
      {
        "type": "WELCOME_SCREEN",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": welcomeScreenId,
        "blockId": welcomeScreenBlockId,
        "content": "Want to see if you know software development?",
        "manditory": false,
        "order": 0,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Let's put your skills to the test.",
        "thirdContent": "Take quiz",
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": 53,
        "media": {
          "provider": "UNSPLASH",
          "id": welcomeMediaId,
          "type": "image",
          "name": "black laptop computer turned on displaying blue screen",
          "link": "https://unsplash.com/photos/N5bT5RctFZ8/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwyMXx8c29mdHdhcmV8ZW58MHx8fHwxNzY0NTcxMDIxfDA",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "author": "Mohammad Rahmani",
          "authorProfile": "https://unsplash.com/@afgprogrammer",
          "downloadLink": "https://api.unsplash.com/photos/N5bT5RctFZ8/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwyMXx8c29mdHdhcmV8ZW58MHx8fHwxNzY0NTcxMDIxfDA"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "EMAIL",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": emailId,
        "blockId": emailBlockId,
        "content": "Before we test your skills, can we grab your email?",
        "manditory": true,
        "order": 1,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "CONTACT_INFO",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": contactInfoId,
        "blockId": contactInfoBlockId,
        "content": "And your name please?",
        "manditory": true,
        "order": 2,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": {
          "first_name": {
            "required": false,
            "visible": true
          },
          "last_name": {
            "required": false,
            "visible": false
          },
          "email": {
            "required": false,
            "visible": false
          },
          "phone": {
            "required": false,
            "visible": false
          },
          "company": {
            "required": false,
            "visible": false
          }
        },
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "MULTIPLE_CHOICE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": multipleChoice1Id,
        "blockId": multipleChoice1BlockId,
        "content": "Which of the following is NOT a programming paradigm?",
        "manditory": true,
        "order": 3,
        "selectOptions": [
          {
            "id": mc1Option1Id,
            "order": 0,
            "value": "Functional"
          },
          {
            "id": mc1Option2Id,
            "order": 1,
            "value": "Declarative"
          },
          {
            "id": mc1Option3Id,
            "order": 2,
            "value": "Circular"
          },
          {
            "id": mc1Option4Id,
            "order": 3,
            "value": "Object-oriented"
          },
          {
            "id": mc1Option5Id,
            "order": 4,
            "value": "Procedural"
          }
        ],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": statement2BlockId,
        "endOnCompletion": false,
        "scoreValues": {
          [mc1Option3Id]: 1,
          [mc1Option4Id]: null,
          [mc1Option1Id]: null,
          [mc1Option2Id]: null,
          [mc1Option5Id]: null
        },
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [
          {
            "conditionType": "IF",
            "actionType": "GO_TO",
            "id": mc1Logic1Id,
            "createdAt": new Date(),
            "updatedAt": new Date(),
            "targetBlockId": statement1BlockId,
            "endOnCompletion": false,
            "parentJourneyItemId": multipleChoice1Id,
            "conditions": [
              {
                "conditionOperation": "IS_EQUAL_TO",
                "additionalType": null,
                "id": mc1Condition1Id,
                "value": mc1Option3Id,
                "createdAt": new Date(),
                "updatedAt": new Date(),
                "sourceBlockId": multipleChoice1BlockId,
                "parentLogicItemId": mc1Logic1Id
              }
            ]
          }
        ]
      },
      {
        "type": "STATEMENT",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": statement1Id,
        "blockId": statement1BlockId,
        "content": "Nailed it!",
        "manditory": false,
        "order": 4,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Circular programming isn't a recognized paradigm — the others are widely used in modern development.",
        "thirdContent": "Keep going",
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": multipleChoice2BlockId,
        "endOnCompletion": false,
        "scoreValues": {
          "no": null,
          "yes": null
        },
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": 53,
        "media": {
          "provider": "UNSPLASH",
          "id": statement1MediaId,
          "type": "image",
          "name": "black flat screen computer monitor",
          "link": "https://unsplash.com/photos/8qEB0fTe9Vw/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHw1fHxzb2Z0d2FyZXxlbnwwfHx8fDE3NjQ1NzEwMjF8MA",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "author": "Mohammad Rahmani",
          "authorProfile": "https://unsplash.com/@afgprogrammer",
          "downloadLink": "https://api.unsplash.com/photos/8qEB0fTe9Vw/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHw1fHxzb2Z0d2FyZXxlbnwwfHx8fDE3NjQ1NzEwMjF8MA"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "STATEMENT",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": statement2Id,
        "blockId": statement2BlockId,
        "content": "Ah darn!",
        "manditory": false,
        "order": 5,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Circular programming isn't a real paradigm — the others are widely used today.",
        "thirdContent": "Keep going",
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": multipleChoice2BlockId,
        "endOnCompletion": false,
        "scoreValues": {
          "no": null,
          "yes": null
        },
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": 53,
        "media": {
          "provider": "UNSPLASH",
          "id": statement2MediaId,
          "type": "image",
          "name": "person using laptop",
          "link": "https://unsplash.com/photos/FlPc9_VocJ4/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwxN3x8Y29tcHV0ZXJ8ZW58MHx8fHwxNzY0NTcxMTA0fDA",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "author": "John",
          "authorProfile": "https://unsplash.com/@johnishappysometimes",
          "downloadLink": "https://api.unsplash.com/photos/FlPc9_VocJ4/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwxN3x8Y29tcHV0ZXJ8ZW58MHx8fHwxNzY0NTcxMTA0fDA"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "MULTIPLE_CHOICE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": multipleChoice2Id,
        "blockId": multipleChoice2BlockId,
        "content": "In Git, what does the command \"git revert\" do?",
        "manditory": true,
        "order": 6,
        "selectOptions": [
          {
            "id": mc2Option1Id,
            "order": 0,
            "value": "Creates a new commit that undoes a previous commit"
          },
          {
            "id": mc2Option2Id,
            "order": 1,
            "value": "Deletes a commit permanently"
          },
          {
            "id": mc2Option3Id,
            "order": 2,
            "value": "Resets your branch to a previous version"
          },
          {
            "id": mc2Option4Id,
            "order": 3,
            "value": "Stashes your current changes"
          }
        ],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": statement4BlockId,
        "endOnCompletion": false,
        "scoreValues": {
          [mc2Option4Id]: null,
          [mc2Option2Id]: null,
          [mc2Option3Id]: null,
          [mc2Option1Id]: 1
        },
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [
          {
            "conditionType": "IF",
            "actionType": "GO_TO",
            "id": mc2Logic1Id,
            "createdAt": new Date(),
            "updatedAt": new Date(),
            "targetBlockId": statement3BlockId,
            "endOnCompletion": false,
            "parentJourneyItemId": multipleChoice2Id,
            "conditions": [
              {
                "conditionOperation": "IS_EQUAL_TO",
                "additionalType": null,
                "id": mc2Condition1Id,
                "value": mc2Option1Id,
                "createdAt": new Date(),
                "updatedAt": new Date(),
                "sourceBlockId": multipleChoice2BlockId,
                "parentLogicItemId": mc2Logic1Id
              }
            ]
          }
        ]
      },
      {
        "type": "STATEMENT",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": statement3Id,
        "blockId": statement3BlockId,
        "content": "Nailed it! ",
        "manditory": false,
        "order": 7,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "git revert creates a new commit that reverses the changes from a previous one.Keep going",
        "thirdContent": "Keep going",
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": multipleChoice3BlockId,
        "endOnCompletion": false,
        "scoreValues": {
          "no": null,
          "yes": null
        },
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": null,
        "media": {
          "provider": "UNSPLASH",
          "id": statement3MediaId,
          "type": "image",
          "name": "black flat screen computer monitor",
          "link": "https://unsplash.com/photos/8qEB0fTe9Vw/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHw1fHxzb2Z0d2FyZXxlbnwwfHx8fDE3NjQ1NzEwMjF8MA",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "author": "Mohammad Rahmani",
          "authorProfile": "https://unsplash.com/@afgprogrammer",
          "downloadLink": "https://api.unsplash.com/photos/8qEB0fTe9Vw/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHw1fHxzb2Z0d2FyZXxlbnwwfHx8fDE3NjQ1NzEwMjF8MA"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "STATEMENT",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": statement4Id,
        "blockId": statement4BlockId,
        "content": "Ah darn!",
        "manditory": false,
        "order": 8,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "git revert creates a new commit that undoes a previous commit. Keep going",
        "thirdContent": "Keep going",
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": multipleChoice3BlockId,
        "endOnCompletion": false,
        "scoreValues": {
          "no": null,
          "yes": null
        },
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": 53,
        "media": {
          "provider": "UNSPLASH",
          "id": statement4MediaId,
          "type": "image",
          "name": "person using laptop",
          "link": "https://unsplash.com/photos/FlPc9_VocJ4/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwxN3x8Y29tcHV0ZXJ8ZW58MHx8fHwxNzY0NTcxMTA0fDA",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "author": "John",
          "authorProfile": "https://unsplash.com/@johnishappysometimes",
          "downloadLink": "https://api.unsplash.com/photos/FlPc9_VocJ4/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwxN3x8Y29tcHV0ZXJ8ZW58MHx8fHwxNzY0NTcxMTA0fDA"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "MULTIPLE_CHOICE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": multipleChoice3Id,
        "blockId": multipleChoice3BlockId,
        "content": "In web development, what does the term \"SSR\" stand for?",
        "manditory": true,
        "order": 9,
        "selectOptions": [
          {
            "id": mc3Option1Id,
            "order": 0,
            "value": "Server-Side Rendering"
          },
          {
            "id": mc3Option2Id,
            "order": 1,
            "value": "Secure System Routing"
          },
          {
            "id": mc3Option3Id,
            "order": 2,
            "value": "Scripted Static Resources"
          }
        ],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": statement6BlockId,
        "endOnCompletion": false,
        "scoreValues": {
          [mc3Option3Id]: null,
          [mc3Option2Id]: null,
          [mc3Option1Id]: 1
        },
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [
          {
            "conditionType": "IF",
            "actionType": "GO_TO",
            "id": mc3Logic1Id,
            "createdAt": new Date(),
            "updatedAt": new Date(),
            "targetBlockId": statement5BlockId,
            "endOnCompletion": false,
            "parentJourneyItemId": multipleChoice3Id,
            "conditions": [
              {
                "conditionOperation": "IS_EQUAL_TO",
                "additionalType": null,
                "id": mc3Condition1Id,
                "value": mc3Option1Id,
                "createdAt": new Date(),
                "updatedAt": new Date(),
                "sourceBlockId": multipleChoice3BlockId,
                "parentLogicItemId": mc3Logic1Id
              }
            ]
          }
        ]
      },
      {
        "type": "STATEMENT",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": statement5Id,
        "blockId": statement5BlockId,
        "content": "You're on fire!",
        "manditory": false,
        "order": 10,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "SSR means Server-Side Rendering.",
        "thirdContent": "Keep going",
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": multipleChoice4BlockId,
        "endOnCompletion": false,
        "scoreValues": {
          "no": null,
          "yes": null
        },
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": 53,
        "media": {
          "provider": "UNSPLASH",
          "id": statement5MediaId,
          "type": "image",
          "name": "black flat screen computer monitor",
          "link": "https://unsplash.com/photos/8qEB0fTe9Vw/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHw1fHxzb2Z0d2FyZXxlbnwwfHx8fDE3NjQ1NzEwMjF8MA",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "author": "Mohammad Rahmani",
          "authorProfile": "https://unsplash.com/@afgprogrammer",
          "downloadLink": "https://api.unsplash.com/photos/8qEB0fTe9Vw/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHw1fHxzb2Z0d2FyZXxlbnwwfHx8fDE3NjQ1NzEwMjF8MA"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "STATEMENT",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": statement6Id,
        "blockId": statement6BlockId,
        "content": "Nope! ",
        "manditory": false,
        "order": 11,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "SSR stands for Server-Side Rendering.",
        "thirdContent": "Keep going",
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": multipleChoice4BlockId,
        "endOnCompletion": false,
        "scoreValues": {
          "no": null,
          "yes": null
        },
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": 53,
        "media": {
          "provider": "UNSPLASH",
          "id": statement6MediaId,
          "type": "image",
          "name": "person using laptop",
          "link": "https://unsplash.com/photos/FlPc9_VocJ4/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwxN3x8Y29tcHV0ZXJ8ZW58MHx8fHwxNzY0NTcxMTA0fDA",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "author": "John",
          "authorProfile": "https://unsplash.com/@johnishappysometimes",
          "downloadLink": "https://api.unsplash.com/photos/FlPc9_VocJ4/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwxN3x8Y29tcHV0ZXJ8ZW58MHx8fHwxNzY0NTcxMTA0fDA"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "MULTIPLE_CHOICE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": multipleChoice4Id,
        "blockId": multipleChoice4BlockId,
        "content": "Which of these best describes a \"pure function\"?",
        "manditory": true,
        "order": 12,
        "selectOptions": [
          {
            "id": mc4Option1Id,
            "order": 0,
            "value": "A function that can only return numbers"
          },
          {
            "id": mc4Option2Id,
            "order": 1,
            "value": "A function that mutates global variables"
          },
          {
            "id": mc4Option3Id,
            "order": 2,
            "value": "A function that has no side effects and always returns the same output for the same input ← Correct"
          },
          {
            "id": mc4Option4Id,
            "order": 3,
            "value": "A function that runs only once"
          }
        ],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": statement8BlockId,
        "endOnCompletion": false,
        "scoreValues": {
          [mc4Option2Id]: null,
          [mc4Option1Id]: null,
          [mc4Option3Id]: 1,
          [mc4Option4Id]: null
        },
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [
          {
            "conditionType": "IF",
            "actionType": "GO_TO",
            "id": mc4Logic1Id,
            "createdAt": new Date(),
            "updatedAt": new Date(),
            "targetBlockId": statement7BlockId,
            "endOnCompletion": false,
            "parentJourneyItemId": multipleChoice4Id,
            "conditions": [
              {
                "conditionOperation": "IS_EQUAL_TO",
                "additionalType": null,
                "id": mc4Condition1Id,
                "value": mc4Option3Id,
                "createdAt": new Date(),
                "updatedAt": new Date(),
                "sourceBlockId": multipleChoice4BlockId,
                "parentLogicItemId": mc4Logic1Id
              }
            ]
          }
        ]
      },
      {
        "type": "STATEMENT",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": statement7Id,
        "blockId": statement7BlockId,
        "content": "Nailed it!",
        "manditory": false,
        "order": 13,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "A pure function is predictable — same input, same output, no side effects.",
        "thirdContent": "Get my score",
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": endScreenBlockId,
        "endOnCompletion": false,
        "scoreValues": {
          "no": null,
          "yes": null
        },
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": 53,
        "media": {
          "provider": "UNSPLASH",
          "id": statement7MediaId,
          "type": "image",
          "name": "black flat screen computer monitor",
          "link": "https://unsplash.com/photos/8qEB0fTe9Vw/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHw1fHxzb2Z0d2FyZXxlbnwwfHx8fDE3NjQ1NzEwMjF8MA",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "author": "Mohammad Rahmani",
          "authorProfile": "https://unsplash.com/@afgprogrammer",
          "downloadLink": "https://api.unsplash.com/photos/8qEB0fTe9Vw/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHw1fHxzb2Z0d2FyZXxlbnwwfHx8fDE3NjQ1NzEwMjF8MA"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "STATEMENT",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "FULL",
        "mobileMediaPosition": "MOBILE_FULL",
        "id": statement8Id,
        "blockId": statement8BlockId,
        "content": "Darn!",
        "manditory": false,
        "order": 14,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "A pure function has no side effects and always produces the same result for the same input.",
        "thirdContent": "Get my score",
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": endScreenBlockId,
        "endOnCompletion": false,
        "scoreValues": {
          "no": null,
          "yes": null
        },
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": 53,
        "media": {
          "provider": "UNSPLASH",
          "id": statement8MediaId,
          "type": "image",
          "name": "person using laptop",
          "link": "https://unsplash.com/photos/FlPc9_VocJ4/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwxN3x8Y29tcHV0ZXJ8ZW58MHx8fHwxNzY0NTcxMTA0fDA",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "author": "John",
          "authorProfile": "https://unsplash.com/@johnishappysometimes",
          "downloadLink": "https://api.unsplash.com/photos/FlPc9_VocJ4/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwxN3x8Y29tcHV0ZXJ8ZW58MHx8fHwxNzY0NTcxMTA0fDA"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      },
      {
        "type": "END_SCREEN",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "SPLIT_RIGHT",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": endScreenId,
        "blockId": endScreenBlockId,
        "content": `{{${contactInfoBlockId}__first_name}}, you scored @score/4!`,
        "manditory": false,
        "order": 15,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Thanks for completing the quiz!",
        "thirdContent": "Learn More",
        "forthContent": "https://www.formsuite.co",
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": true,
        "scoreValues": null,
        "responseTableVisible": null,
        "horizontalAlignment": true,
        "opacity": 100,
        "media": {
          "provider": "PEXEL",
          "id": endScreenMediaId,
          "type": "video",
          "name": "5495781",
          "link": "https://videos.pexels.com/video-files/5495781/5495781-hd_1366_576_30fps.mp4",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "thumbnail": "https://images.pexels.com/videos/5495781/pictures/preview-0.jpg"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": []
      }
    ],
    "snippets": [],
    "metaTitle": "Digital Marketing Quiz",
    "gdprSetting": "NEVER",
    "gdprRequired": false,
    "consentMessageTitle": "I consent to the collection of my personal data"
  };
}

export default generateSoftwareDevelopmentQuizTemplate;
